import { ReduxState } from "~/types/redux";
import {
  ChatbotState,
  ProactiveModalContent,
  ProactiveModalContentByTrigger,
  isProactiveModalContentByTrigger,
} from "~/types/chatbot";
import type { ChatTriggerType } from "~/hooks/chatbot/useChatTriggers";

/** Maps the kebab-case ChatTriggerType values produced by useChatTriggers to the
 *  camelCase keys used by ProactiveModalContentByTrigger / Monarch's ChatbotTriggers. */
const TRIGGER_KEY_BY_TYPE: Partial<Record<ChatTriggerType, keyof Omit<ProactiveModalContentByTrigger, "default">>> = {
  idle: "idle",
  "back-button": "backButton",
  "vas-uncertainty": "vasUncertainty",
  "product-uncertainty": "productUncertainty",
  "form-error": "formError",
};

const isNonEmptyProactiveModalContent = (value: ProactiveModalContent | undefined): value is ProactiveModalContent =>
  !!value && typeof value === "object" && !Array.isArray(value) && Object.keys(value).length > 0;

/**
 * Default chatbot state used as fallback when chatbot state is undefined
 */
const DEFAULT_CHATBOT_STATE: ChatbotState = {
  status: "DISABLED",
  triggerType: null,
  proactiveModalViewed: {},
  gigaEventTriggered: {},
  vasCounter: 0,
  productCounter: 0,
  vasTriggeredPages: {},
  productTriggeredPages: {},
};

/**
 * Select entire chatbot state with fallback to defaults
 */
export const selectChatbotState = (state: ReduxState): ChatbotState => state.chatbot ?? DEFAULT_CHATBOT_STATE;

/**
 * Select chatbot status (DISABLED, HIDDEN, GIGA_CHAT_VISIBLE, PROACTIVE_CHAT_VISIBLE)
 */
export const selectChatbotStatus = (state: ReduxState): ChatbotState["status"] => selectChatbotState(state).status;

/**
 * Select chatbot trigger type
 */
export const selectChatbotTriggerType = (state: ReduxState): ChatbotState["triggerType"] =>
  selectChatbotState(state).triggerType;

/**
 * Select proactive modal viewed pages
 */
export const selectProactiveModalViewed = (state: ReduxState): Record<string, boolean> =>
  selectChatbotState(state).proactiveModalViewed ?? {};

/**
 * Select giga event triggered pages
 */
export const selectGigaEventTriggered = (state: ReduxState): Record<string, boolean> =>
  selectChatbotState(state).gigaEventTriggered ?? {};

/**
 * Select VAS counter
 */
export const selectVasCounter = (state: ReduxState): number => selectChatbotState(state).vasCounter;

/**
 * Select product counter
 */
export const selectProductCounter = (state: ReduxState): number => selectChatbotState(state).productCounter;

/**
 * Select VAS triggered pages
 */
export const selectVasTriggeredPages = (state: ReduxState): Record<string, boolean> =>
  selectChatbotState(state).vasTriggeredPages ?? {};

/**
 * Select product triggered pages
 */
export const selectProductTriggeredPages = (state: ReduxState): Record<string, boolean> =>
  selectChatbotState(state).productTriggeredPages ?? {};

/**
 * Check if a specific page has already triggered VAS threshold
 */
export const selectIsPageVasTriggered = (state: ReduxState, page: string): boolean =>
  selectVasTriggeredPages(state)[page] ?? false;

/**
 * Check if a specific page has already triggered product threshold
 */
export const selectIsPageProductTriggered = (state: ReduxState, page: string): boolean =>
  selectProductTriggeredPages(state)[page] ?? false;

/**
 * Check if chatbot is visible (not DISABLED or HIDDEN)
 */
export const selectIsChatbotVisible = (state: ReduxState): boolean => {
  const status = selectChatbotStatus(state);
  return status === "GIGA_CHAT_VISIBLE" || status === "PROACTIVE_CHAT_VISIBLE";
};

export const selectProactiveModalContent = (
  state: ReduxState
): ProactiveModalContent | ProactiveModalContentByTrigger | undefined =>
  selectChatbotState(state).proactiveModalContent;

/**
 * Returns the modal copy to render for the current `triggerType`. When the
 * Monarch config provides a per-trigger map, the matching variant is returned
 * with `default` as the fallback. Legacy flat shape is returned as-is so
 * existing prod variants keep working without changes.
 */
export const selectProactiveModalContentForTrigger = (state: ReduxState): ProactiveModalContent | undefined => {
  const content = selectProactiveModalContent(state);
  if (!content) return undefined;

  if (!isProactiveModalContentByTrigger(content)) {
    return isNonEmptyProactiveModalContent(content) ? content : undefined;
  }

  const triggerType = selectChatbotTriggerType(state);
  const key = triggerType ? TRIGGER_KEY_BY_TYPE[triggerType as ChatTriggerType] : undefined;
  const perTrigger = key ? content[key] : undefined;
  if (isNonEmptyProactiveModalContent(perTrigger)) return perTrigger;
  return isNonEmptyProactiveModalContent(content.default) ? content.default : undefined;
};
